/**
 * The phone's own storage: a tiny key-value store on IndexedDB.
 *
 * IndexedDB rather than localStorage because it survives the PWA being
 * closed and the browser clearing "site data it does not need", holds the
 * whole item list without hitting a 5 MB ceiling, and is written
 * asynchronously so saving an invoice never freezes the screen. Where it
 * is missing (a private window, a test) everything still works in memory
 * -- the screen says the phone cannot keep data, rather than failing.
 */
const DB = 'bizvision-van'
const STORE = 'kv'

let opening: Promise<IDBDatabase | null> | null = null
const memory = new Map<string, unknown>()

function open(): Promise<IDBDatabase | null> {
  if (opening) return opening
  opening = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') { resolve(null); return }
      const req = indexedDB.open(DB, 1)
      req.onupgradeneeded = () => { req.result.createObjectStore(STORE) }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
      req.onblocked = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
  return opening
}

/** True when what is saved will still be here after the app is closed. */
export async function persistent(): Promise<boolean> {
  return (await open()) !== null
}

export async function get<T>(key: string): Promise<T | undefined> {
  const db = await open()
  if (!db) return memory.get(key) as T | undefined
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(key)
      req.onsuccess = () => resolve(req.result as T | undefined)
      req.onerror = () => resolve(memory.get(key) as T | undefined)
    } catch {
      resolve(memory.get(key) as T | undefined)
    }
  })
}

export async function set(key: string, value: unknown): Promise<void> {
  memory.set(key, value)
  const db = await open()
  if (!db) return
  await new Promise<void>((resolve, reject) => {
    try {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).put(value, key)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    } catch (e) {
      reject(e)
    }
  })
}

export async function del(key: string): Promise<void> {
  memory.delete(key)
  const db = await open()
  if (!db) return
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).delete(key)
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
    } catch {
      resolve()
    }
  })
}

/** Ask the browser not to clear this site's data under storage pressure. */
export async function askToKeep(): Promise<boolean> {
  try {
    return (await navigator.storage?.persist?.()) ?? false
  } catch {
    return false
  }
}
